import flowplayer from '@flowplayer/player';
import { useGlobalConfig } from 'context/GlobalContext';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import theme from 'theme';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';

import { useAutoNext } from '../AutoNext';
import { VideoInfoView } from '../Video/UI/Info';
import { useSubtitlesSettings } from '../Video/UI/hooks/SubtitlesContext';
import { usePlayer } from '../context';

import Button from 'components/Button';
import { Icons } from 'components/Icons';
import { useProgram } from 'components/Program/context';
import { WebDiv } from 'components/WebDiv';

const BACK_EVENT = 'back-remote-control';

let BROWSING_MENU_TIMER: NodeJS.Timeout;

export function CustomControls(props: { api: any; exit: () => void }) {
  const {
    isPlaying,
    media,
    play,
    pause,
    isLive,
    nextTrack,
    textTracks,
    setTextTrack,
    setLastWatchedMediaTime,
  } = usePlayer();
  const flowplayerUI = props.api.parentElement?.querySelector('flowplayer-ui')!;
  const { setEnabled, enabled, setColor, color, setFontSize, fontSize } = useSubtitlesSettings();
  const program = useProgram();
  const analytics = useAnalytics();
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [isBrowsingMenu, setIsBrowsingMenu] = React.useState(true);
  const [isAudioSelectionOpen, setIsAudioSelectionOpen] = React.useState(false);
  const [isSubtitleSelecionOpen, setIsSubtitleSelecionOpen] = React.useState(false);
  const [originalSubtitleElement, setOriginalSubtitleElement] = React.useState<any>(null);
  const [isSubtitleConfigOpen, setIsSubtitleConfigOpen] = React.useState(false);
  const { hasInternet } = useGlobalConfig();

  const sliderRef = React.useRef<HTMLDivElement>(null);
  const TogglePlay = isPlaying ? Icons.SolidPause : Icons.Play;
  const opts = React.useMemo(() => props.api.opts as any, [props.api]);
  const isOtherModalOpen = isAudioSelectionOpen || isSubtitleSelecionOpen;
  const availableAudioTracks = React.useMemo(() => opts.asel.tracks, [opts]);
  const { autoNextCard, showNextTrack } = useAutoNext({ currentTime, duration });

  const toggleAudioSelection = () => setIsAudioSelectionOpen(!isAudioSelectionOpen);
  const toggleSubtitleSelection = () => setIsSubtitleSelecionOpen(!isSubtitleSelecionOpen);
  const toggleSubtitleConfig = () => setIsSubtitleConfigOpen(!isSubtitleConfigOpen);

  const isPlayPauseFocused = () => document.querySelector('.player-play-pause-button .focused');

  const subtitleColors = ['white', 'yellow', 'red'] as const;
  const subtitleFontSizes = ['small', 'medium', 'large'] as const;

  const formattedCurrentTime = React.useMemo(
    () => new Date(currentTime * 1000).toISOString().slice(11, 19),
    [currentTime]
  );

  const formattedDuration = React.useMemo(
    () => new Date(duration * 1000).toISOString().slice(11, 19),
    [duration]
  );

  const currentTimePercentage = React.useMemo(
    () => ((currentTime * 100) / duration).toFixed(2),
    [currentTime, duration]
  );

  useEffect(() => {
    if (hasInternet || !currentTime) return;
    setLastWatchedMediaTime(currentTime);
  }, [hasInternet]);

  const togglePlay = () => {
    if (isPlaying) {
      pause();
      props.api.togglePlay();
      analytics.triggerMediaPauseActionEvent({
        programId: program.id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programCategory: program.category?.map((tag) => tag.name).join(', '),
        programProducer: program.ads?.adunit?.name,
        programDirector: program.director,
        programGenre: program.genre.map((genre) => genre.name).join(', '),
        programYear: program.year,
      });
      return;
    }

    analytics.triggerMediaPlayActionEvent({
      programId: program.id,
      programTitle: program.title,
      programAgeRating: program.rating,
      programAdUnit: program.ads?.adunit?.val!,
      programCategory: program.category?.map((tag) => tag.name).join(', '),
      programProducer: program.ads?.adunit?.name,
      programDirector: program.director,
      programGenre: program.genre.map((genre) => genre.name).join(', '),
      programYear: program.year,
    });

    props.api.togglePlay();
    play();
  };

  const handleMetadataLoaded = React.useCallback(() => {
    setDuration(props.api.duration);
    if (isPlaying) {
      play();
      props.api.play();
    }
  }, [props.api.duration]);

  const handleCurrentTimeUpdate = React.useCallback(
    () => setCurrentTime(props.api.currentTime),
    [props.api.currentTime]
  );

  const handleOnLivePress = () => {
    if (props.api.seekable.end(0)) {
      props.api.currentTime = props.api.seekable.end(0);
      props.api.play();
    }
  };

  const changeAudio = (id: string) => {
    const event = new CustomEvent('asel:id', { detail: id });
    props.api.dispatchEvent(event);
    setIsAudioSelectionOpen(false);
  };

  const sliderOnKeyPress = React.useCallback(
    (e: KeyboardEvent) => {
      //left
      if (e.keyCode === 37) {
        const newCurrentTime = props.api.currentTime - 15;

        if (newCurrentTime < 0) {
          props.api.currentTime = 0;
          return;
        }

        props.api.currentTime = newCurrentTime;
        return;
      }

      //right
      if (e.keyCode === 39) {
        const newCurrentTime = props.api.currentTime + 15;

        if (newCurrentTime > props.api.duration) {
          props.api.currentTime = props.api.duration;
          return;
        }

        props.api.currentTime = newCurrentTime;
      }
    },
    [props.api]
  );

  const onKeyPress = React.useCallback(
    (e: KeyboardEvent) => {
      const playPauseFocused = isPlayPauseFocused();

      const isBack = [8, 27, 461, 10009].includes(e.keyCode);
      const isEnterOrPlayPause = [13, 10252].includes(e.keyCode);

      if (isBack && !isBrowsingMenu) {
        e.preventDefault();
        e.stopPropagation();
        handleBack();
        return;
      }

      if (isEnterOrPlayPause && !isBrowsingMenu && (playPauseFocused || isPlaying)) {
        e.preventDefault();
        e.stopPropagation();
        togglePlay();
        return;
      }

      clearTimeout(BROWSING_MENU_TIMER);
      BROWSING_MENU_TIMER = setTimeout(() => setIsBrowsingMenu(false), 5000);
      if (isPlaying && !isBrowsingMenu) setIsBrowsingMenu(true);
      if (sliderRef.current?.classList.contains('focused')) sliderOnKeyPress(e);
    },
    [isPlaying, isBrowsingMenu, sliderOnKeyPress]
  );

  const handleBack = () => props?.exit?.();

  // reset is browsing
  useEffect(() => {
    BROWSING_MENU_TIMER = setTimeout(() => setIsBrowsingMenu(false), 5000);
    return () => clearTimeout(BROWSING_MENU_TIMER);
  }, []);

  useEffect(() => {
    window.addEventListener(BACK_EVENT, handleBack);
    return () => window.removeEventListener(BACK_EVENT, handleBack);
  }, []);

  // remove original controls
  useEffect(() => {
    const subtitlesComponent = props.api.parentElement?.querySelector('flowplayer-subtitles-menu')!;
    (subtitlesComponent as any).style.display = 'none';
    setOriginalSubtitleElement(subtitlesComponent);

    props.api.parentElement?.querySelector('flowplayer-header-right-zone')?.remove();
    props.api.parentElement?.querySelector('flowplayer-header-duration')?.remove();
    props.api.parentElement?.querySelector('flowplayer-control-buttons')?.remove();
    props.api.parentElement?.querySelector('flowplayer-live-status')?.remove();
    props.api.parentElement?.querySelector('flowplayer-elapsed-time')?.remove();
    props.api.parentElement?.querySelector('flowplayer-timeline-bar')?.remove();
    props.api.parentElement?.querySelector('flowplayer-control-duration')?.remove();
    props.api.parentElement?.querySelector('flowplayer-volume-control')?.remove();
    props.api.parentElement?.querySelector('flowplayer-quality-menu')?.remove();
    props.api.parentElement?.querySelector('.fp-menu-container')?.remove();

    props.api.on(flowplayer.events.METADATA, handleMetadataLoaded);
    props.api.on(flowplayer.events.TIME_UPDATE, handleCurrentTimeUpdate);

    return () => {
      props.api.off(flowplayer.events.METADATA, handleMetadataLoaded);
      props.api.off(flowplayer.events.TIME_UPDATE, handleCurrentTimeUpdate);
    };
  }, [handleCurrentTimeUpdate, handleMetadataLoaded, props.api]);

  // append keypress listener
  useEffect(() => {
    window.addEventListener('keydown', onKeyPress, false);
    return () => window.removeEventListener('keydown', onKeyPress);
  }, [onKeyPress]);

  // force play when isPlaying flag changes
  useEffect(() => {
    if (isPlaying) {
      props.api.play();
    }
  }, [props.api, isPlaying]);

  if (isPlaying && !isBrowsingMenu && !showNextTrack) {
    return createPortal(
      <WebDiv className="navigation-container navigation-priority">
        <Pressable accessibilityRole="button" onPress={() => {}} />
      </WebDiv>,
      flowplayerUI
    );
  }

  return createPortal(
    <WebDiv
      className={`navigation-container player-controls-container ${
        isOtherModalOpen ? '' : 'navigation-priority'
      }`}>
      <View style={styles.mainContainer}>
        <LinearGradient
          colors={['transparent', '#000000bc']}
          locations={[0.1, 0.4]}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={styles.headerGradient}>
          <Pressable accessibilityRole="button" onPress={handleBack} style={styles.backButton}>
            <Icons.TurnBack
              width={calcSizeForDevice(60).toString()}
              height={calcSizeForDevice(60).toString()}
            />
          </Pressable>

          <VideoInfoView {...media!} />
        </LinearGradient>

        {autoNextCard}

        <LinearGradient
          end={{ x: 0, y: 1 }}
          locations={[0.1, 0.4]}
          start={{ x: 0, y: 0 }}
          colors={[showNextTrack && nextTrack ? '#000000bc' : 'transparent', '#000000bc']}
          style={styles.footerGradient}>
          {/* <VideoUnmuteButton /> */}

          <View style={styles.controlsContainer}>
            <WebDiv
              ref={sliderRef}
              className={
                isLive
                  ? 'navigation container navigation-ignore'
                  : 'touchable ignore-left-key ignore-right-key'
              }>
              {duration && currentTime && !isLive ? (
                <View style={styles.timeContainer}>
                  <Text style={styles.timeText}>{formattedCurrentTime}</Text>

                  <Text style={styles.timeText}>{formattedDuration}</Text>
                </View>
              ) : null}

              <View style={styles.progressBarContainer}>
                <View
                  style={[
                    styles.progressBar,
                    {
                      /**
                       * Fix the progress bar width type issue by converting the currentTimePercentage to a string
                       * and appending a '%' symbol to it.
                       */
                      backgroundColor: isLive ? theme.colors.error[500] : theme.colors.primary[500],
                      width: isLive ? '100%' : parseFloat(currentTimePercentage) + '%',
                      zIndex: 10,
                    } as any, // Type assertion to handle percentage string
                  ]}
                />

                <View
                  style={[
                    styles.progressBackground,
                    {
                      backgroundColor: theme.colors.primary[100],
                    },
                  ]}
                />
              </View>
            </WebDiv>

            <View style={[styles.controlsRow, { marginBottom: calcSizeForTv(10) }]}>
              <WebDiv className="player-play-pause-button">
                <Pressable accessibilityRole="button" onPress={togglePlay}>
                  <TogglePlay
                    width={calcSizeForDevice(40).toString()}
                    height={calcSizeForDevice(40).toString()}
                  />
                </Pressable>
              </WebDiv>

              {isLive && (
                <Button
                  variant="outline"
                  borderColor={theme.colors.error[500]}
                  iconType="SolidCircle"
                  iconStyle={{
                    color:
                      !isPlaying || props.api.playerState['is-live-seeked']
                        ? '#888'
                        : theme.colors.error[500],
                  }}
                  textStyle={{ marginLeft: calcSizeForTv(20), fontSize: calcSizeForTv(24) }}
                  style={{
                    borderStyle: 'solid',
                    borderWidth: calcSizeForTv(2),
                    borderRadius: calcSizeForTv(10),
                    height: calcSizeForTv(40),
                    alignItems: 'center',
                    flexDirection: 'row',
                  }}
                  onPress={() => handleOnLivePress()}>
                  Ao vivo
                </Button>
              )}

              <Pressable
                accessibilityRole="button"
                onPress={toggleAudioSelection}
                style={styles.audioButton}>
                <Icons.AudioLibrary
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>

              {textTracks?.length !== 0 && (
                <Pressable accessibilityRole="button" onPress={toggleSubtitleSelection}>
                  <Icons.Subtitles
                    width={calcSizeForDevice(40).toString()}
                    height={calcSizeForDevice(40).toString()}
                  />
                </Pressable>
              )}

              {textTracks?.length !== 0 && enabled && (
                <Pressable accessibilityRole="button" onPress={toggleSubtitleConfig}>
                  <Icons.TwoSliders
                    width={calcSizeForDevice(40).toString()}
                    height={calcSizeForDevice(40).toString()}
                  />
                </Pressable>
              )}
            </View>
          </View>
        </LinearGradient>
      </View>

      {isAudioSelectionOpen && (
        <View
          style={{
            padding: 16,
            zIndex: 200,
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}>
          <WebDiv className="navigation-container navigation-priority">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <Pressable accessibilityRole="button" onPress={toggleAudioSelection}>
                <Icons.ArrowLeft
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>

              <Text style={{ color: 'white', fontFamily: theme.fonts.inter.bold, fontSize: 18 }}>
                Seleção de áudio
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 16, marginTop: 16 }}>
              {availableAudioTracks.map((audio: any) => {
                return (
                  <Button
                    variant="solid"
                    role="button"
                    style={{ justifyContent: 'center' }}
                    onPress={() => changeAudio(audio.id)}>
                    {audio.title}
                  </Button>
                );
              })}
            </View>
          </WebDiv>
        </View>
      )}

      {isSubtitleSelecionOpen && (
        <View
          style={{
            padding: 16,
            zIndex: 200,
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}>
          <WebDiv className="navigation-container navigation-priority">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <Pressable accessibilityRole="button" onPress={toggleSubtitleSelection}>
                <Icons.ArrowLeft
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>

              <Text style={{ color: 'white', fontFamily: theme.fonts.inter.bold, fontSize: 18 }}>
                Seleção de legenda
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 16, marginTop: 16 }}>
              {textTracks?.length !== 0 && (
                <>
                  {textTracks?.map((textTrack: any) => {
                    return (
                      <Button
                        key={textTrack.id}
                        role="button"
                        variant="solid"
                        style={{ justifyContent: 'center' }}
                        onPress={() => {
                          toggleSubtitleSelection();
                          setEnabled(true);
                          setTextTrack(textTrack);
                        }}>
                        {textTrack.title}
                      </Button>
                    );
                  })}
                  <Button
                    role="button"
                    variant="solid"
                    style={{ justifyContent: 'center' }}
                    onPress={() => {
                      toggleSubtitleSelection();
                      setEnabled(false);
                      setTextTrack(undefined);
                    }}>
                    Desativar Legenda
                  </Button>
                </>
              )}
            </View>
          </WebDiv>
        </View>
      )}

      {isSubtitleConfigOpen && (
        <View
          style={{
            padding: 16,
            zIndex: 200,
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}>
          <WebDiv className="navigation-container navigation-priority">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <Pressable accessibilityRole="button" onPress={toggleSubtitleConfig}>
                <Icons.ArrowLeft
                  width={calcSizeForDevice(40).toString()}
                  height={calcSizeForDevice(40).toString()}
                />
              </Pressable>

              <Text style={{ color: 'white', fontFamily: theme.fonts.inter.bold, fontSize: 18 }}>
                Configuração de legenda
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 16, marginTop: 16, alignItems: 'center' }}>
              <Text style={{ color: 'white', fontFamily: theme.fonts.inter.bold, fontSize: 18 }}>
                Cor da legenda
              </Text>
              {subtitleColors.map((subtitleColor) => (
                <Button
                  role="button"
                  borderColor={color === subtitleColor ? 'white' : 'transparent'}
                  variant="solid"
                  key={subtitleColor}
                  style={{
                    justifyContent: 'center',
                  }}
                  onPress={() => {
                    if (color === subtitleColor) {
                      return;
                    }
                    setColor(subtitleColor);
                  }}>
                  <Text
                    style={[
                      {
                        color: color === subtitleColor ? 'white' : 'gray',
                        fontSize: 24,
                      },
                    ]}>
                    {subtitleColor === 'white'
                      ? 'Branco'
                      : subtitleColor === 'yellow'
                      ? 'Amarelo'
                      : 'Vermelho'}
                  </Text>
                </Button>
              ))}
              <Text style={{ color: 'white', fontFamily: theme.fonts.inter.bold, fontSize: 18 }}>
                Tamanho da legenda
              </Text>
              {subtitleFontSizes.map((subtitleFontSize) => (
                <Button
                  role="button"
                  variant="solid"
                  key={subtitleFontSize}
                  style={{ justifyContent: 'center' }}
                  onPress={() => {
                    if (fontSize === subtitleFontSize) {
                      return;
                    }
                    setFontSize(subtitleFontSize);
                  }}>
                  <Text
                    style={{
                      color: fontSize === subtitleFontSize ? 'white' : 'gray',
                      fontSize:
                        subtitleFontSize === 'small' ? 24 : subtitleFontSize === 'medium' ? 32 : 40,
                    }}>
                    {subtitleFontSize === 'small'
                      ? 'Pequena'
                      : subtitleFontSize === 'medium'
                      ? 'Média'
                      : 'Grande'}
                  </Text>
                </Button>
              ))}
            </View>
          </WebDiv>
        </View>
      )}
    </WebDiv>,
    flowplayerUI
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    zIndex: 100,
    flexDirection: 'column',
    top: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  headerGradient: {
    padding: calcSizeForTv(16),
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  backButton: {
    marginRight: calcSizeForTv(40),
  },
  footerGradient: {
    marginTop: 'auto',
    height: calcSizeForTv(200),
  },
  controlsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
  },
  timeContainer: {
    margin: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  timeText: {
    color: 'white',
    fontFamily: theme.fonts.inter.bold,
    fontSize: 18,
  },
  progressBarContainer: {
    margin: 16,
    height: 5,
    position: 'relative',
    borderRadius: 9999,
    overflow: 'hidden',
  },
  progressBar: {
    height: 5,
    position: 'absolute',
  },
  progressBackground: {
    height: 5,
    width: '100%',
    zIndex: 5,
    borderRadius: 9999,
    position: 'absolute',
  },
  controlsRow: {
    flexDirection: 'row',
    gap: 42,
    padding: 16,
  },
  audioButton: {
    marginLeft: 'auto',
  },
});
